import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { ATTRIBUTION_TAB_IDS } from './consts';
import { PLATFORM_TEMPLATE_POSTBACK_URL_PATTERNS } from './platformTemplateDefaults';
import IntegrationRequestService from './service';

const TAB_ID = ATTRIBUTION_TAB_IDS.POSTBACK_OVERALL_SETUP;
const URL_VALIDATE_DEBOUNCE_MS = 400;

const service = new IntegrationRequestService();

// Debounced POST /api/partner-integration/request/validate-url/ — mirrors WebsiteField.
// Returns null while empty/skipped/pending, true/false once resolved.
const useApiUrlValidity = (value, skip = false) => {
    const [valid, setValid] = useState(null);
    const seqRef = useRef(0);
    useEffect(() => {
        const trimmed = (value || '').trim();
        if (skip || !trimmed) {
            setValid(null);
            return undefined;
        }
        const seq = ++seqRef.current;
        const timer = setTimeout(() => {
            service
                .validatePostbackUrl(trimmed)
                .then(res => {
                    if (seq === seqRef.current) setValid(Boolean(res && res.valid));
                })
                .catch(() => {
                    if (seq === seqRef.current) setValid(false);
                });
        }, URL_VALIDATE_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [value, skip]);
    return valid;
};

// Hooks must be called in a stable order → one call per fixed URL field.
export const useFieldValidityValues = formData => {
    const tab = formData?.[TAB_ID] || {};
    // Affiliate templates assemble URLs from a fixed pattern + user-typed domain. The FE already
    // gates the domain via POSTBACK_URL_DOMAIN_REGEX, so the live `validate-url` route adds no
    // signal — skip the network call entirely for those.
    const platformTemplate = formData?.[ATTRIBUTION_TAB_IDS.GENERAL]?.platform_template;
    const skip = !!PLATFORM_TEMPLATE_POSTBACK_URL_PATTERNS[platformTemplate];
    return {
        postback_url_base: useApiUrlValidity(tab.postback_url_base, skip),
        conversions_postback_url: useApiUrlValidity(tab.conversions_postback_url, skip),
        post_install_events_postback_url: useApiUrlValidity(tab.post_install_events_postback_url, skip),
        revenue_events_postback_url: useApiUrlValidity(tab.revenue_events_postback_url, skip),
        fraud_postbacks_postback_url: useApiUrlValidity(tab.fraud_postbacks_postback_url, skip),
    };
};

const FieldValidityContext = createContext({});

export const FieldValidityProvider = FieldValidityContext.Provider;

export const useFieldValidityContext = () => useContext(FieldValidityContext);
